import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { faImage } from "@fortawesome/free-solid-svg-icons";
import { apiClient } from "../../api/client";
import { Button } from "../../ui/components/Button";

type Frame = {
  shortId: string;
  owner: string;
  ownerEmoji?: string | null;
  claimedAt?: string;
  status: string;
};
type FramesPayload = { asOf: string; count: number; frames: Frame[] };
type MyDevice = { deviceId: string; shortId?: string; status: string };

const Section = styled.section`
  max-width: 1100px;
  margin: 32px auto 16px;
  padding: 0 16px;
  border-top: 1px solid rgba(35, 35, 35, 0.15);
  padding-top: 24px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const HeaderLabel = styled.span`
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: #414141;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderCount = styled.span`
  font-size: 0.75rem;
  color: #5f5f5f;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const frameCardStyles = css`
  background: #ffffff;
  border: 1px solid #232323;
  box-shadow: 4px 4px 0 #232323;
  padding: 10px 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono",
    "Courier New", monospace;
  font-size: 0.8rem;
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: transform 0.12s ease, box-shadow 0.12s ease;

  &:hover {
    transform: translate(-1px, -1px);
    box-shadow: 5px 5px 0 #232323;
  }
`;

const FrameCard = styled.div`
  ${frameCardStyles}
`;

// your own frame: the frame-button colours, and it opens the frame's page
const MyFrameCard = styled(Link)`
  ${frameCardStyles}
  background: #ffe4b5;
  border-color: #6b4f00;
  box-shadow: 4px 4px 0 #6b4f00;
  color: inherit;
  text-decoration: none;

  &:hover {
    box-shadow: 5px 5px 0 #6b4f00;
  }
`;

const YoursTag = styled.span`
  margin-left: auto;
  font-size: 0.65rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
  color: #6b4f00;
`;

const CardLine = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  line-height: 1.35;
`;

const EmojiSpan = styled.span`
  font-size: 1rem;
  line-height: 1;
`;

const ShortIdSpan = styled.span`
  color: #5f5f5f;
`;

const OwnerSpan = styled.span`
  color: #414141;
`;

const Footnote = styled.p`
  margin: 16px 0 0;
  font-size: 0.7rem;
  color: #5f5f5f;
  text-align: center;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
`;

const AotdIrlPublicFramesSection: React.FC<{ isAuthenticated: boolean }> = ({
  isAuthenticated,
}) => {
  const navigate = useNavigate();
  const [data, setData] = useState<FramesPayload | null>(null);
  const [myDevices, setMyDevices] = useState<MyDevice[]>([]);

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await apiClient.get<FramesPayload>("/api/aotd-irl/public/frames", {
          signal: ctrl.signal,
        });
        if (!ctrl.signal.aborted) setData(res.data);
      } catch {
        // swallow — non-essential section
      }
    })();
    return () => ctrl.abort();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setMyDevices([]);
      return;
    }
    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await apiClient.get<{ devices?: MyDevice[] }>("/api/aotd-irl/devices", {
          signal: ctrl.signal,
        });
        if (ctrl.signal.aborted) return;
        setMyDevices((res.data?.devices ?? []).filter((d) => d.status === "registered"));
      } catch {
        // non-essential
      }
    })();
    return () => ctrl.abort();
  }, [isAuthenticated]);

  const frames = data?.frames ?? [];
  if (!frames.length && !myDevices.length) {
    return null;
  }

  const count = data?.count ?? 0;
  const myDeviceIdByShortId = new Map(
    myDevices.filter((d) => d.shortId).map((d) => [d.shortId as string, d.deviceId])
  );

  return (
    <Section aria-label="AOTD-IRL frames on display">
      <HeaderRow>
        <HeaderLabel>AOTD-IRL FRAMES</HeaderLabel>
        <HeaderRight>
          <HeaderCount>{count} live</HeaderCount>
          {myDevices.length > 0 && (
            <Button
              icon={faImage}
              bgColor="#ffe4b5"
              textColor="#6b4f00"
              borderColor="#6b4f00"
              onClick={() => navigate("/aotd-irl/my")}
              showText
            >
              {myDevices.length === 1 ? "my frame" : `my frames (${myDevices.length})`}
            </Button>
          )}
        </HeaderRight>
      </HeaderRow>
      <Grid>
        {frames.map((frame) => {
          const myDeviceId = myDeviceIdByShortId.get(frame.shortId);
          const line = (
            <CardLine>
              {frame.ownerEmoji ? <EmojiSpan aria-hidden>{frame.ownerEmoji}</EmojiSpan> : null}
              <ShortIdSpan>[{frame.shortId}]</ShortIdSpan>
              <OwnerSpan>· @{frame.owner}</OwnerSpan>
              {myDeviceId ? <YoursTag>yours</YoursTag> : null}
            </CardLine>
          );
          if (myDeviceId) {
            return (
              <MyFrameCard
                key={frame.shortId}
                to={`/aotd-irl/my/${encodeURIComponent(myDeviceId)}`}
                aria-label={`your frame ${frame.shortId}`}
              >
                {line}
              </MyFrameCard>
            );
          }
          return <FrameCard key={frame.shortId}>{line}</FrameCard>;
        })}
      </Grid>
      <Footnote>displayed irl by their owners — proof of physical display</Footnote>
    </Section>
  );
};

export default AotdIrlPublicFramesSection;
